import React, { useEffect, useId, useRef, useState } from 'react';
import {
  Bot,
  Box,
  BrainCircuit,
  ChevronDown,
  FileSearch,
  FolderKanban,
  Landmark,
  LineChart,
  LibraryBig,
  Network,
  Search,
  Sparkles,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { getAccessibleCompanyAiWorkspaceTabs, type CompanyAiWorkspaceTabId } from './companyAiWorkspaceTabs';
import './CompanyAiQuickMenu.css';

type CompanyAiQuickMenuProps = {
  userEmail?: string | null;
  userRoles?: string[] | null;
};

const TAB_ICONS: Record<CompanyAiWorkspaceTabId, React.ComponentType<{ size?: number; strokeWidth?: number; 'aria-hidden'?: boolean }>> = {
  'ask-ai': Sparkles,
  'ai-search': Search,
  'pm-agent': Bot,
  'graph-rag': Network,
  finance: LineChart,
  reconcile: Landmark,
  dbyd: FileSearch,
  'online-quote-files': FolderKanban,
  'file-document': LibraryBig,
  'project-files': FolderKanban,
  'ifc-models': Box,
};

export default function CompanyAiQuickMenu({ userEmail, userRoles }: CompanyAiQuickMenuProps) {
  const panelId = useId();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const tabs = getAccessibleCompanyAiWorkspaceTabs(userRoles, userEmail);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  return (
    <div ref={containerRef} className="company-ai-quick-menu">
      <button
        type="button"
        className="company-ai-quick-menu__trigger"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => setIsOpen((current) => !current)}
      >
        <BrainCircuit size={17} strokeWidth={2.1} aria-hidden />
        <span>Company AI</span>
        <ChevronDown className="company-ai-quick-menu__chevron" size={15} strokeWidth={2.2} aria-hidden />
      </button>

      {isOpen ? (
        <div id={panelId} className="company-ai-quick-menu__panel" aria-label="Company AI quick access">
          <div className="company-ai-quick-menu__heading">
            <span className="company-ai-quick-menu__eyebrow">Quick access</span>
            <strong>Company AI tools</strong>
          </div>
          <nav className="company-ai-quick-menu__grid" aria-label="Company AI tools">
            {tabs.map((tab) => {
              const Icon = TAB_ICONS[tab.id];
              return (
                <Link
                  key={tab.id}
                  className="company-ai-quick-menu__item"
                  to={`/ai?tab=${tab.id}`}
                  onClick={() => setIsOpen(false)}
                >
                  <span className="company-ai-quick-menu__icon"><Icon size={18} strokeWidth={2} aria-hidden /></span>
                  <span className="company-ai-quick-menu__copy">
                    <strong>{tab.label}</strong>
                    <span>{tab.description}</span>
                  </span>
                </Link>
              );
            })}
          </nav>
        </div>
      ) : null}
    </div>
  );
}
